import {
  DiscoverMoreProduct,
  DiscoverMoreProductRowTile,
  TileStatus,
  VxlCardGroupDiscoverMoreTile as SchemaVxlCardGroupDiscoverMoreTile,
} from '@varo/graphql-schema-types';
import { TileAdapterType } from 'features/home/HomeScreen/adapters/adapterMap/types';
import { DiscoverMoreSectionProps } from 'features/home/HomeScreen/components/DiscoverMoreSection/DiscoverMoreSection';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import { ListItemGroupItem } from 'shared/components/vxl/ListItemGroup/ListItemGroup';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import { Maybe } from 'shared/utils/types';

export type VxlCardGroupDiscoverMoreTile = Omit<
  SchemaVxlCardGroupDiscoverMoreTile,
  'tiles'
> & {
  discoverTiles: DiscoverMoreProductRowTile[];
};

export interface DiscoverMoreTileNode extends VXLComponent {
  id: string;
  name: VXLComponentId.DISCOVER_MORE_SECTION;
  props: DiscoverMoreSectionProps;
}

export const ProductTypeIcons: Record<DiscoverMoreProduct, IconKeyType> = {
  [DiscoverMoreProduct.DirectDeposit]: 'CALENDAR',
  [DiscoverMoreProduct.BelieveCard]: 'CREDIT_SCORE',
  [DiscoverMoreProduct.SavingsAccount]: 'PIGGY_BANK',
  [DiscoverMoreProduct.VaroAdvance]: 'DEPOSIT_CASH',
  [DiscoverMoreProduct.LineOfCredit]: 'LOAN',
  [DiscoverMoreProduct.PayOverTime]: 'MONEY',
};

export const ProductTypeRoutes: Record<DiscoverMoreProduct, string> = {
  [DiscoverMoreProduct.DirectDeposit]: '/direct-deposit-benefits',
  [DiscoverMoreProduct.BelieveCard]: '/believe/apply',
  [DiscoverMoreProduct.SavingsAccount]: '/open-savings',
  [DiscoverMoreProduct.VaroAdvance]: '/varo-advance',
  [DiscoverMoreProduct.LineOfCredit]:
    '/varo-line-of-credit/line-of-credit-entry-point',
  [DiscoverMoreProduct.PayOverTime]: '/pay-over-time/landing-page',
};

const DISCOVER_MORE_ROUTE = '/discover-more';

export const mapProductRowToListItem = (
  tile: DiscoverMoreProductRowTile,
  dependencies: HomeCardDependencies,
): ListItemGroupItem => {
  const iconKey = ProductTypeIcons[tile.productType];
  const route = ProductTypeRoutes[tile.productType];

  return {
    keyId: tile.id,
    primaryCopy: tile.title,
    secondaryCopy: tile.description,
    leadingAccessory: iconKey
      ? {
          id: 'icon',
          name: VXLComponentId.ICON,
          props: { iconKey },
        }
      : null,
    showsChevron: true,
    analyticsId: 'discoverMoreProductClicked',
    customAttributes: {
      productType: tile.productType,
    },
    onPress: () => {
      if (route) {
        dependencies.navigateToRoute({
          route,
          analyticsEvent: tile.onClickAnalytics ?? null,
        });
      }
    },
  };
};

const discoverMoreTile: TileAdapterType<VxlCardGroupDiscoverMoreTile> = (
  tile: VxlCardGroupDiscoverMoreTile,
  dependencies: HomeCardDependencies,
): Maybe<DiscoverMoreTileNode> => {
  if (tile.status !== TileStatus.Loaded || !tile.discoverTiles?.length) {
    return null;
  }

  const items = tile.discoverTiles.map((productRow) =>
    mapProductRowToListItem(productRow, dependencies),
  );

  const viewMoreButton = tile.viewMoreButton
    ? {
        label: tile.viewMoreButton.label,
        onPress: () => {
          dependencies.navigateToRoute({
            route: DISCOVER_MORE_ROUTE,
            analyticsEvent: tile.viewMoreButton?.onClickAnalytics ?? null,
          });
        },
      }
    : null;

  return {
    id: tile.id,
    name: VXLComponentId.DISCOVER_MORE_SECTION,
    props: {
      title: tile.title,
      items,
      viewMoreButton,
    },
  };
};

export default discoverMoreTile;
